import IconClosePanel from '@bimser/icons/16/close-panel';
import IconSave from '@bimser/icons/16/save';
import IconSubMenuArrow from '@bimser/icons/16/sub-menu-arrow';
import * as React from 'react';
import {
	BCButton,
	BCButtonOverflow,
	BCInputSearch,
	BCLabel,
	BCList,
	BCLoading,
	BCScrollbars,
	BCSplitModal,
	findMessage,
	IInputChangeEventArgs
} from '../';
import { PropertyInspectorComponent } from '../BCPropertyInspector';
import Styles from './assets/listEditorStyle.scss';
import { IListEditorItem, IListEditorItemProps, IListEditorLayoutProps } from './entities';
import Item from './Item';

const overflowButtonProps = { type: 'text' as const };
const scrollBarStyle: React.CSSProperties = { height: '100%' };

const ListEditorLayout = (props: IListEditorLayoutProps) => {
	const onDelete = (item: IListEditorItem) => {
		props.onDelete?.(item);
	};

	const onSelect = (item: IListEditorItem) => {
		props.onSelect?.(item);
	};

	const onSelectDelete = (item: IListEditorItem) => {
		props.onSelectDelete?.(item);
	};

	const onSearch = (args: IInputChangeEventArgs) => {
		props.onSearch?.(args);
	};

	const renderItem = (item: IListEditorItem) => {
		const ItemTemplate = props.itemTemplate || Item;
		return (
			<ItemTemplate
				externalProps={props.itemExternalProps}
				externalData={item.externalData}
				icon={item.icon}
				iconType={item.iconType || 'icon'}
				id={item.id}
				key={item.id}
				selected={item.selected}
				showDelete={item.showDelete}
				showSelectDelete={item.showSelectDelete}
				subText={item.subText}
				subTextPrefix={item.subTextPrefix}
				text={item.text}
				onDelete={onDelete}
				onSelect={onSelect}
				onSelectDelete={onSelectDelete}
				renderCustomListItemText={props.renderCustomListItemText}
			/>
		);
	};

	const renderList = () => (
		<div className={Styles.list}>
			<BCInputSearch
				key="search_input"
				onChange={onSearch}
				placeHolder={findMessage.get('100002')}
			/>
			<span className={Styles.title}>
				{props.listTitle}
				{renderMaxLabel()}
			</span>
			<BCScrollbars styles={scrollBarStyle}>
				<BCList
					dataSource={props.items}
					bordered={false}
					split={false}
					renderItem={renderItem}
					sortable={props.sortable}
					dropItem={props.dropItem}
					moveItem={props.moveItem}
					customEmpty={props.listCustomEmpty}
				/>
			</BCScrollbars>
		</div>
	);

	const renderMaxLabel = () => {
		if (props.maxItemCount) {
			return (
				<i>{`(${findMessage.get('101855')} ${props.maxItemCount}/${
					props.items.length
				})`}</i>
			);
		}
	};

	const renderPropertyEditor = () => (
		<div className={Styles.viewer}>
			<div className={Styles.inspector}>
				<PropertyInspectorComponent
					currentLanguage={props.currentLanguage}
					supportedLanguages={props.supportedLanguages}
					searchable={props.searchable}
					structure={props.structure}
					data={props.data}
					fireAction={props.openCollection as any}
					isModal
					getOptionsPromise={props.getOptionsPromise}
					renderCustomEmpty={props.renderCustomEmptyPI}
					emptyTemplateSubText={props.emptyTemplateSubText}
					onRefresh={props.onRefresh}
					onValueChanged={props.onChangeData}
					guid={props.id}
				/>
			</div>
		</div>
	);

	const renderSubTitle = () => {
		const subTitle = props.subTitle?.split('/');
		return subTitle?.map((x, index) => {
			if (index === subTitle.length - 1) {
				return <span key={`${x}-${index}`}>{x}</span>;
			}
			return (
				<React.Fragment key={`${x}-${index}`}>
					<span>{x}</span>
					<span>
						<IconSubMenuArrow
							width="16px"
							height="16px"
						/>
					</span>
				</React.Fragment>
			);
		});
	};

	const renderHeader = () => (
		<div className={Styles.layoutHeader}>
			<div className={Styles.titleBox}>
				<BCLabel>{props.title}</BCLabel>
				<br />
				<div className={Styles.subTitle}>{renderSubTitle()}</div>
			</div>
			<div className={Styles.ribbonPanel}>
				<BCButtonOverflow
					items={props.leftRibbonItems}
					alignRight
					overflowButtonProps={overflowButtonProps}
				/>
			</div>
			{renderHeaderContent()}
		</div>
	);

	const renderHeaderContent = () => (
		<div className={Styles.header}>
			<BCButton
				icon={<IconClosePanel />}
				onClick={props.onCancelClick}
				type={'text'}
				className={Styles.closeButton}
			/>
			<BCButton
				icon={<IconSave />}
				onClick={props.onOkClick}
				type={'text'}
			>
				{findMessage.get('100620')}
			</BCButton>
		</div>
	);

	return (
		<BCLoading
			delay={500}
			show={props.showLoading}
		>
			<BCSplitModal
				cssClassName={Styles.listEditor + (props.className ? ' ' + props.className : '')}
				leftSideWidth={props.leftSideWidth}
				rightSideWidth={props.rightSideWidth}
				renderHeader={renderHeader}
				renderLeftSide={props.renderLeftSide || renderList}
				renderRightSide={props.renderRightSide || renderPropertyEditor}
				onCancelClick={props.onCancelClick}
				onOkClick={props.onOkClick}
				useDefaultButtons={props.useDefaultButtons}
			/>
		</BCLoading>
	);
};
export default ListEditorLayout;
export { IStatefulTreeEditorState } from './entities';
export {
	IStatefulListEditorProps,
	IStatefulListEditorState,
	default as StatefulListEditor
} from './StatefulListEditor';
export { default as StatefulTreeEditor } from './StatefulTreeEditor';
export { IListEditorItem, IListEditorItemProps, IListEditorLayoutProps, Item };
